import React, { useState, useEffect } from 'react';
import { 
  Table, 
  Button, 
  Space, 
  Tag, 
  Card,
  Row,
  Col,
  Statistic,
  Select,
  Input,
  message,
  Alert,
  Drawer,
  Descriptions,
  Modal
} from 'antd';
import { 
  ReloadOutlined,
  UserOutlined,
  TeamOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined
} from '@ant-design/icons';
import request from '../../../utils/request';

const { Option } = Select;
const { Search } = Input;

interface UserPermissionOverview {
  id: number;
  username: string;
  email: string;
  role: string;
  role_display: string;
  is_active: boolean;
  is_active_display: boolean;
  last_login: string;
  profile_info: {
    customer_sub_role?: string;
    staff_sub_role?: string;
    customer_code?: string;
    can_view_sensitive_info: boolean;
    can_view_financial_info: boolean;
    can_view_statistics: boolean;
    statistics_level: string;
  };
  permissions_info: {
    is_admin: boolean;
    is_hq_staff: boolean;
    is_warehouse_supervisor: boolean;
    is_warehouse_staff: boolean;
    is_main_account: boolean;
    is_sub_account: boolean;
  };
  assignments_info: {
    warehouses: number[];
    projects: number[];
    total_assignments: number;
  };
  special_permissions_info: {
    sensitive: boolean;
    financial: boolean;
    statistics: boolean;
  };
}

const UserPermissionOverview: React.FC = () => {
  const [users, setUsers] = useState<UserPermissionOverview[]>([]);
  const [loading, setLoading] = useState(false);
  const [roleFilter, setRoleFilter] = useState<string>('');
  const [searchText, setSearchText] = useState<string>('');
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const [total, setTotal] = useState<number>(0);
  const [summary, setSummary] = useState<{ total: number; active: number; admin: number; staff: number; customer: number }>({ total: 0, active: 0, admin: 0, staff: 0, customer: 0 });
  const [detailDrawerOpen, setDetailDrawerOpen] = useState(false);
  const [detailUser, setDetailUser] = useState<UserPermissionOverview | null>(null);
  const [assignmentsModalOpen, setAssignmentsModalOpen] = useState(false);
  const [assignmentsUser, setAssignmentsUser] = useState<UserPermissionOverview | null>(null);

  const ROLE_CHOICES = [
    { value: 'admin', label: '管理员' },
    { value: 'staff', label: '员工' },
    { value: 'customer', label: '客户' },
  ];

  useEffect(() => {
    fetchUsers(1, pageSize, searchText, roleFilter);
    fetchSummary();
  }, [roleFilter]);

  const fetchUsers = async (page: number = currentPage, size: number = pageSize, keyword: string = searchText, role: string = roleFilter) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (role) params.append('role', role);
      if (keyword) params.append('search', keyword);
      params.append('page', String(page));
      params.append('page_size', String(size));
      const response = await request.get(`/api/v1/permission-management/users/?${params.toString()}`);
      const list = response.data?.results || response.data || [];
      setUsers(list);
      setTotal(response.data?.count ?? list.length);
      setCurrentPage(page);
      setPageSize(size);
    } catch (error) {
      message.error('获取用户权限概览失败');
      console.error('获取用户权限概览失败:', error);
    } finally {
      setLoading(false);
    }
  };

  const buildParams = (overrides: Record<string, string | number | undefined>) => {
    const params = new URLSearchParams();
    Object.entries(overrides).forEach(([k, v]) => {
      if (v !== undefined && v !== '') params.append(k, String(v));
    });
    return params.toString();
  };

  const fetchCount = async (role?: string, isActive?: boolean) => {
    const query = buildParams({ role, is_active: isActive ? 'true' : undefined, page: 1, page_size: 1 });
    const res = await request.get(`/api/v1/permission-management/users/?${query}`);
    return res.data?.count ?? (Array.isArray(res.data) ? res.data.length : 0);
  };

  // 顶部统计使用全局口径：不随筛选与搜索变化
  const fetchSummary = async () => {
    try {
      const [totalCnt, activeCnt, adminCnt, staffCnt, customerCnt] = await Promise.all([
        fetchCount(undefined, undefined),
        fetchCount(undefined, true),
        fetchCount('admin', undefined),
        fetchCount('staff', undefined),
        fetchCount('customer', undefined),
      ]);
      setSummary({ total: totalCnt, active: activeCnt, admin: adminCnt, staff: staffCnt, customer: customerCnt });
    } catch (e) {
      // 忽略错误，保持现状
    }
  };

  const handleSearch = (value: string) => {
    setSearchText(value);
    fetchUsers(1, pageSize, value, roleFilter);
    // 统计保持全局口径，不随搜索变化
  };

  const filteredUsers = users.filter(user => {
    if (searchText) {
      return user.username.toLowerCase().includes(searchText.toLowerCase()) ||
             user.email.toLowerCase().includes(searchText.toLowerCase());
    }
    return true;
  });

  const openDetailDrawer = (record: UserPermissionOverview) => {
    setDetailUser(record);
    setDetailDrawerOpen(true);
  };

  const renderPermissionBadges = (record: UserPermissionOverview) => (
    <div>
      {record.permissions_info.is_admin && <Tag color="red">管理员</Tag>}
      {record.permissions_info.is_hq_staff && <Tag color="blue">总部员工</Tag>}
      {record.permissions_info.is_warehouse_supervisor && <Tag color="orange">仓库主管</Tag>}
      {record.permissions_info.is_warehouse_staff && <Tag color="cyan">仓库员工</Tag>}
      {record.permissions_info.is_main_account && <Tag color="green">主账号</Tag>}
      {record.permissions_info.is_sub_account && <Tag color="purple">子账号</Tag>}
    </div>
  );

  const getRoleDisplayName = (role: string) => {
    const roleMap: Record<string, string> = {
      admin: '管理员',
      hq_staff: '总部员工',
      warehouse_supervisor: '仓库主管',
      warehouse_staff: '仓库员工',
      customer: '客户',
    };
    return roleMap[role] || role;
  };

  const getSubRoleDisplayName = (subRole: string) => {
    const subRoleMap: Record<string, string> = {
      main_account: '主账号',
      sub_account: '子账号',
    };
    return subRoleMap[subRole] || subRole;
  };

  const columns = [
    {
      title: '用户信息',
      key: 'user_info',
      width: 200,
      render: (_, record: UserPermissionOverview) => (
        <div>
          <div style={{ fontWeight: 'bold' }}>{record.username}</div>
          <div style={{ fontSize: '12px', color: '#666' }}>{record.email}</div>
          <div style={{ fontSize: '12px', color: '#666' }}>
            {getRoleDisplayName(record.role)}
            {record.profile_info?.customer_sub_role && 
              ` (${getSubRoleDisplayName(record.profile_info.customer_sub_role)})`
            }
            {record.profile_info?.staff_sub_role && 
              ` (${getSubRoleDisplayName(record.profile_info.staff_sub_role)})`
            }
          </div>
        </div>
      ),
    },
    {
      title: '状态',
      dataIndex: 'is_active',
      key: 'is_active',
      width: 80,
      render: (isActive: boolean) => (
        <Tag color={isActive ? 'green' : 'red'}>
          {isActive ? '激活' : '禁用'}
        </Tag>
      ),
    },
    {
      title: '权限信息',
      key: 'permissions',
      width: 150,
      render: (_, record: UserPermissionOverview) => renderPermissionBadges(record),
    },
    {
      title: '特殊权限',
      key: 'special_permissions',
      width: 120,
      render: (_, record: UserPermissionOverview) => (
        <div>
          {record.special_permissions_info.sensitive && <Tag color="red">敏感信息</Tag>}
          {record.special_permissions_info.financial && <Tag color="gold">财务信息</Tag>}
          {record.special_permissions_info.statistics && <Tag color="blue">统计信息</Tag>}
        </div>
      ),
    },
    {
      title: '数据分配',
      key: 'assignments',
      width: 100,
      render: (_, record: UserPermissionOverview) => (
        <div>
          <div>
            <a onClick={() => { setAssignmentsUser(record); setAssignmentsModalOpen(true); }}>仓库: {record.assignments_info.warehouses.length}</a>
          </div>
          <div>
            <a onClick={() => { setAssignmentsUser(record); setAssignmentsModalOpen(true); }}>项目: {record.assignments_info.projects.length}</a>
          </div>
        </div>
      ),
    },
    {
      title: '最后登录',
      dataIndex: 'last_login',
      key: 'last_login',
      width: 120,
      render: (date: string) => date ? new Date(date).toLocaleDateString() : '从未登录',
    },
    {
      title: '操作',
      key: 'actions',
      width: 100,
      render: (_: any, record: UserPermissionOverview) => (
        <Button size="small" onClick={() => openDetailDrawer(record)}>查看权限</Button>
      ),
    },
  ];

  // 统计数据
  const totalUsers = summary.total;
  const activeUsers = summary.active;
  const adminUsers = summary.admin;
  const staffUsers = summary.staff;
  const customerUsers = summary.customer;

  return (
    <div className="user-permission-overview">
      <Row gutter={16} wrap={false} style={{ marginBottom: 24 }}>
        {[{
          title: '总用户数', value: totalUsers, prefix: <UserOutlined />
        },{
          title: '激活用户', value: activeUsers, prefix: <CheckCircleOutlined />, valueStyle: { color: '#3f8600' }
        },{
          title: '管理员', value: adminUsers, prefix: <TeamOutlined />
        },{
          title: '员工用户', value: staffUsers, prefix: <UserOutlined />
        },{
          title: '客户用户', value: customerUsers, prefix: <UserOutlined />
        }].map((stat, idx) => (
          <Col key={idx} flex="1 1 0">
            <Card>
              <Statistic title={stat.title} value={stat.value} prefix={stat.prefix} valueStyle={stat.valueStyle as any} />
            </Card>
          </Col>
        ))}
      </Row>

      <Card>
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message="用户概览 - 使用说明"
          description={
            <div>
              <div>纵览用户的角色、特殊能力与已分配的数据。</div>
              <div>支持按角色筛选和搜索，有助于核对某用户是否具备期望权限。</div>
              <div style={{ marginTop: 6, color: '#374151' }}>
                注：本页重点展示“特定用户”的个体差异信息（如：
                <strong>特殊权限</strong> 与 <strong>已分配的数据</strong>）。
                角色模板中定义的通用权限（资源 × 动作 × 范围）请前往“角色管理/权限矩阵”查看与维护。
              </div>
            </div>
          }
        />
        <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between' }}>
          <Space>
            <Select
              placeholder="按角色筛选"
              style={{ width: 150 }}
              allowClear
              onChange={setRoleFilter}
            >
              {ROLE_CHOICES.map(role => (
                <Option key={role.value} value={role.value}>
                  {role.label}
                </Option>
              ))}
            </Select>
            <Search
              placeholder="搜索用户名或邮箱"
              style={{ width: 200 }}
              onSearch={handleSearch}
              allowClear
              onChange={(e) => {
                const v = e?.target?.value ?? '';
                setSearchText(v);
                if (v === '') {
                  fetchUsers(1, pageSize, '', roleFilter);
                }
              }}
            />
          </Space>
          <Button 
            icon={<ReloadOutlined />}
            onClick={() => fetchUsers(1, pageSize, searchText, roleFilter)}
            loading={loading}
          >
            刷新
          </Button>
        </div>

        <Table
          columns={columns}
          dataSource={users}
          loading={loading}
          rowKey="id"
          pagination={{
            current: currentPage,
            pageSize,
            total,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total) => `共 ${total} 条记录`,
            onChange: (p, s) => fetchUsers(p, s || pageSize, searchText, roleFilter),
            onShowSizeChange: (_p, s) => fetchUsers(1, s, searchText, roleFilter),
          }}
          scroll={{ x: 900 }}
        />
      </Card>

      <Drawer
        title={detailUser ? `权限明细 - ${detailUser.username}` : '权限明细'}
        open={detailDrawerOpen}
        width={520}
        onClose={() => setDetailDrawerOpen(false)}
      >
        {detailUser && (
          <div>
            <Descriptions column={1} size="small" bordered>
              <Descriptions.Item label="用户名">{detailUser.username}</Descriptions.Item>
              <Descriptions.Item label="邮箱">{detailUser.email || '—'}</Descriptions.Item>
              <Descriptions.Item label="角色">{getRoleDisplayName(detailUser.role)}</Descriptions.Item>
              {detailUser.profile_info?.customer_sub_role && (
                <Descriptions.Item label="客户子角色">{getSubRoleDisplayName(detailUser.profile_info.customer_sub_role)}</Descriptions.Item>
              )}
              {detailUser.profile_info?.staff_sub_role && (
                <Descriptions.Item label="员工子角色">{getSubRoleDisplayName(detailUser.profile_info.staff_sub_role)}</Descriptions.Item>
              )}
              <Descriptions.Item label="特殊权限">
                <div>
                  {detailUser.special_permissions_info.sensitive && <Tag color="red">敏感</Tag>}
                  {detailUser.special_permissions_info.financial && <Tag color="gold">财务</Tag>}
                  {detailUser.special_permissions_info.statistics && <Tag color="blue">统计</Tag>}
                  {!detailUser.special_permissions_info.sensitive && !detailUser.special_permissions_info.financial && !detailUser.special_permissions_info.statistics && '—'}
                </div>
              </Descriptions.Item>
              <Descriptions.Item label="权限标签">{renderPermissionBadges(detailUser)}</Descriptions.Item>
              <Descriptions.Item label="分配-仓库">{(detailUser.assignments_info.warehouses || []).join(', ') || '—'}</Descriptions.Item>
              <Descriptions.Item label="分配-项目">{(detailUser.assignments_info.projects || []).join(', ') || '—'}</Descriptions.Item>
            </Descriptions>
            <div style={{ marginTop: 12, color: '#666' }}>
              如需查看资源-动作-范围的完整矩阵，请前往“权限矩阵”或在“角色管理”里检查模板。
            </div>
          </div>
        )}
      </Drawer>

      <Modal
        title={assignmentsUser ? `分配详情 - ${assignmentsUser.username}` : '分配详情'}
        open={assignmentsModalOpen}
        onCancel={() => setAssignmentsModalOpen(false)}
        footer={null}
        width={560}
      >
        {assignmentsUser ? (
          <div style={{ display: 'flex', gap: 24 }}>
            <div style={{ flex: 1 }}>
              <div style={{ marginBottom: 6, color: '#666' }}>仓库ID列表</div>
              <div style={{ whiteSpace: 'pre-wrap' }}>
                {(assignmentsUser.assignments_info.warehouses || []).join(', ') || '—'}
              </div>
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ marginBottom: 6, color: '#666' }}>项目ID列表</div>
              <div style={{ whiteSpace: 'pre-wrap' }}>
                {(assignmentsUser.assignments_info.projects || []).join(', ') || '—'}
              </div>
            </div>
          </div>
        ) : null}
      </Modal>
    </div>
  );
};

export default UserPermissionOverview;
